import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import LangToggle from "@/components/LangToggle";
import BillingPanel from "@/components/BillingPanel";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { PRICING_COPY } from "@/i18n/pricing";
import { PLAN_RANK } from "@/lib/plans";
import { CheckIcon, LightningIcon, SparkleIcon, BroadcastIcon } from "@phosphor-icons/react";

const PLANS = [
  { id: "free", price: "$0", accent: "#FFFFFF", icon: LightningIcon },
  { id: "pro", price: "$9.99", accent: "#0A84FF", highlight: true, icon: SparkleIcon },
  { id: "press_box_pro", price: "$299", accent: "#FDB927", icon: BroadcastIcon },
];

function getPlanCardBorder(plan) {
  if (plan.highlight) return "border-[#0A84FF]";
  if (plan.id === "press_box_pro") return "border-[#FDB927]/50";
  return "border-white/10";
}

function getPlanCtaClass(plan, isCurrent) {
  if (isCurrent) return "bg-white/5 text-white/40";
  if (plan.id === "press_box_pro") return "bg-[#FDB927] hover:bg-yellow-400 text-black";
  return "bg-[#0A84FF] hover:bg-blue-600 text-white";
}

function paidCtaLabel(c, pc, { isCurrent, isLoading, subscribed, confirming, included }) {
  if (isCurrent) return c.onPlan;
  if (included) return c.included;
  if (isLoading) return subscribed ? c.switching : c.redirecting;
  if (confirming) return c.confirmSwitch;
  return subscribed ? c.switchTo : pc.cta;
}

function freeCtaLabel(c, isCurrent, billing, confirming, included) {
  if (isCurrent) return c.currentFree;
  if (included) return c.included;
  if (billing?.cancel_at_period_end) return c.freeScheduled;
  if (confirming) return c.confirmFree;
  if (billing?.has_subscription) return c.toFree;
  return c.useFree;
}

export default function Pricing() {
  const { user, refresh } = useAuth();
  const { lang } = useLang();
  const c = PRICING_COPY[lang];
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const [polling, setPolling] = useState(false);
  const [status, setStatus] = useState(params.get("canceled") ? "canceled" : null);
  const [billing, setBilling] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const highlightPlan = params.get("plan");

  const loadBilling = useCallback(async () => {
    if (!user) return;
    try {
      const r = await api.get("/billing/me");
      setBilling(r.data);
    } catch {
      setBilling(null);
    }
  }, [user]);

  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  const afterChange = async () => {
    await refresh();
    await loadBilling();
  };

  // Poll if returning from Stripe
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    setPolling(true);
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/payments/status/${sid}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          setPolling(false);
          await refresh();
          return;
        }
        if (r.data.status === "expired" || attempts >= 10) {
          setStatus(r.data.status === "expired" ? "expired" : "pending");
          setPolling(false);
          return;
        }
        setTimeout(tick, 2000);
      } catch {
        setStatus("error");
        setPolling(false);
      }
    };
    tick();
  }, [params, refresh]);

  const startCheckout = async (planId) => {
    if (!user) {
      navigate("/login");
      return;
    }
    if (billing?.has_subscription) {
      if (confirming !== planId) {
        setConfirming(planId);
        return;
      }
      setLoading(planId);
      try {
        await api.post("/payments/change-plan", { plan_id: planId });
        setStatus("switched");
        await afterChange();
      } catch {
        setStatus("error");
      } finally {
        setLoading(null);
        setConfirming(null);
      }
      return;
    }
    setLoading(planId);
    try {
      const res = await api.post("/payments/checkout", {
        plan_id: planId,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch {
      setLoading(null);
      setStatus("checkoutError");
    }
  };

  const toFree = async () => {
    if (!billing?.has_subscription) return navigate("/dashboard");
    if (billing.cancel_at_period_end) return undefined;
    if (confirming !== "free") return setConfirming("free");
    setConfirming(null);
    await api.post("/payments/cancel", { resume: false });
    return afterChange();
  };

  const Wrapper = user ? AppLayout : LandingShell;

  return (
    <Wrapper>
      <div className="mb-8 text-center fade-up">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#0A84FF] mb-2">{c.kicker}</p>
        <h1 className="font-display font-bold text-4xl md:text-6xl uppercase tracking-tighter" data-testid="pricing-title">{c.title}</h1>
        <p className="text-white/60 text-sm md:text-base mt-3 max-w-2xl mx-auto">{c.desc}</p>
        {status && (
          <div className={`mt-4 inline-block px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider ${status === "paid" || status === "switched" ? "bg-[#32D74B]/15 text-[#32D74B]" : "bg-white/10 text-white/70"}`} data-testid="checkout-status">
            {c.status[status] || c[status]}
          </div>
        )}
        {polling && <p className="mt-3 text-xs text-white/50">{c.verifying}</p>}
      </div>

      <BillingPanel billing={billing} copy={c} lang={lang} onChange={afterChange} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {PLANS.map((p) => {
          const pc = c.plans[p.id];
          const isCurrent = user?.plan === p.id;
          const included = !!user && !billing?.has_subscription && (PLAN_RANK[user.plan] ?? 0) > PLAN_RANK[p.id];
          return (
            <div key={p.id} data-testid={`plan-${p.id}`} className={`relative p-6 rounded-md bg-[#121212] border transition-colors ${getPlanCardBorder(p)} ${highlightPlan === p.id && !isCurrent ? "ring-2 ring-offset-2 ring-offset-[#050505] ring-[#FDB927]" : ""}`}>
              {p.highlight && (
                <span className="absolute -top-3 left-6 px-2.5 py-1 rounded bg-[#0A84FF] text-white text-[10px] font-black uppercase tracking-[0.2em]">{c.mostPopular}</span>
              )}
              {pc.badge && (
                <span className="absolute -top-3 left-6 px-2.5 py-1 rounded bg-[#FDB927] text-black text-[10px] font-black uppercase tracking-[0.2em]">{pc.badge}</span>
              )}
              <p.icon weight="fill" size={26} className="mb-4" style={{ color: p.accent }} />
              <h3 className="font-display font-bold text-3xl uppercase tracking-tighter">{pc.name}</h3>
              <div className="mt-2 mb-5 flex items-end gap-1">
                <span className="font-num text-5xl font-black">{p.price}</span>
                <span className="text-white/40 text-sm mb-1">{pc.cadence}</span>
              </div>
              <ul className="space-y-2 mb-6">
                {pc.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-white/80">
                    <CheckIcon weight="bold" size={16} className="text-[#32D74B] mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              {p.id === "free" ? (
                <button
                  disabled={isCurrent || included || billing?.cancel_at_period_end}
                  data-testid="plan-cta-free"
                  className={`w-full py-3 rounded-md text-xs font-bold uppercase tracking-wider ${isCurrent || included || billing?.cancel_at_period_end ? "bg-white/5 text-white/40" : "bg-white/10 text-white hover:bg-white/20"}`}
                  onClick={toFree}
                >
                  {freeCtaLabel(c, isCurrent, billing, confirming === "free", included)}
                </button>
              ) : (
                <button
                  data-testid={`plan-cta-${p.id}`}
                  disabled={loading === p.id || isCurrent || included}
                  onClick={() => startCheckout(p.id)}
                  className={`w-full py-3 rounded-md text-xs font-bold uppercase tracking-wider ${getPlanCtaClass(p, isCurrent || included)}`}
                >
                  {paidCtaLabel(c, pc, { isCurrent, isLoading: loading === p.id, subscribed: !!billing?.has_subscription, confirming: confirming === p.id, included })}
                </button>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-center text-xs text-white/40 mt-8">{c.footer}</p>
    </Wrapper>
  );
}

// For unauthenticated visitors: simple shell with header
function LandingShell({ children }) {
  const { lang } = useLang();
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-1.5 shrink-0" data-testid="brand-logo">
            <img src="/gamly-logo.png" alt="Gamly AI" className="h-7 sm:h-9 w-auto" />
            <span className="hidden sm:inline text-[9px] font-black uppercase tracking-[0.12em] text-[#0A84FF] bg-[#0A84FF]/12 border border-[#0A84FF]/30 rounded px-1 py-0.5 leading-none">Beta</span>
          </Link>
          <div className="flex items-center gap-3 sm:gap-4">
            <LangToggle />
            <Link to="/login" data-testid="pricing-signin-link" className="text-xs font-bold uppercase tracking-wider text-[#0A84FF] whitespace-nowrap">{PRICING_COPY[lang].signin}</Link>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">{children}</div>
    </div>
  );
}
